import { BillOfLadingError, MiddleMilePartner, SortationGaylordError } from "@deliverr/sortation-client";
import { createSuccessNotification, logError, logStart } from "@deliverr/ui-facility";
import { useCommonFlow } from "@deliverr/ui-facility/lib-facility/flow/useCommonFlow";
import { useCallback, useMemo, useRef, useState } from "react";
import { useAsyncFn, useMount, useUnmount } from "react-use";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { navbarState } from "sortation/base/navbarState";
import { sortationIdState } from "sortation/base/sortationIdState";
import { useSortationClient } from "sortation/base/useSortationClient";
import { useSortationModal } from "sortation/components/SortationModal";
import { isGaylord, isValidShipwellBolId } from "sortation/utils/stringValidation";
import { isQuinceSortCode } from "sortation/utils/quinceUtils";
import { TOAST_AUTO_CLOSE } from "sortation/utils/toastAutoClose";
import { TextButton } from "../../components/TextButton";
import { loadToTrailerMessageMap, LoadToTrailerModal } from "./modals";
import { useQuery } from "sortation/hooks/useQuery";

export enum LoadToTrailerScanState {
  AWAITING_BOL_SCAN = "AWAITING_BOL_SCAN",
  AWAITING_TRAILER_INPUT = "AWAITING_TRAILER_INPUT",
  AWAITING_GAYLORD_SCAN = "AWAITING_GAYLORD_SCAN",
  AWAITING_TRIP_ID_INPUT = "AWAITING_TRIP_ID_INPUT",
}

const GROUND_MIDDLE_MILE_PARTNER_OPTIONS: MiddleMilePartner[] = [MiddleMilePartner.TRANSLOOP, MiddleMilePartner.SGL];

export const useLoadToTrailerScan = () => {
  // Global State
  const setNavbar = useSetRecoilState(navbarState);

  // Local State
  const [bolId, setBolId] = useState<string>("");
  const [trailerId, setTrailerId] = useState<string>("");
  const [gaylordId, setGaylordId] = useState<string>("");
  const [tripId, setTripId] = useState<string>("");
  const [loadToTrailerScanState, setLoadToTrailerScanState] = useState<LoadToTrailerScanState>(
    LoadToTrailerScanState.AWAITING_BOL_SCAN
  );
  const pendingGaylordIdRef = useRef<string>("");
  const pendingIsAirRef = useRef<boolean>(false);
  const tripIdLabelRef = useRef<string>("Enter Booking ID");

  // Hooks
  const { addAutoCloseNotification, resetNotifications, successResponse, infoResponse, emitFlash } = useCommonFlow();
  const { showModal, hideModal } = useSortationModal();
  const sortationId = useRecoilValue(sortationIdState);
  const sortationClient = useSortationClient();
  const query = useQuery();

  useMount(async () => {
    // BOL ID param used when redirected here from Dock Door scan
    const bolIdParam = query.get("bolId");
    if (bolIdParam) {
      await onBolIdSubmit(bolIdParam);
    }
  });

  useUnmount(() => {
    resetNotifications();
    setNavbar({});
  });

  const resetPage = useCallback(() => {
    resetNotifications();
    setBolId("");
    setTrailerId("");
    setGaylordId("");
    setTripId("");
    pendingGaylordIdRef.current = "";
    pendingIsAirRef.current = false;
    setLoadToTrailerScanState(LoadToTrailerScanState.AWAITING_BOL_SCAN);

    setNavbar({
      title: "Bol/Trailer Scan",
      rightAction: undefined,
    });
  }, [setNavbar, resetNotifications]);

  const handleError = useCallback(
    (err) => {
      const errCode = err.message;

      let modal: LoadToTrailerModal;

      switch (errCode) {
        case SortationGaylordError.NOT_FOUND:
          modal = LoadToTrailerModal.GAYLORD_NOT_FOUND;
          break;
        case SortationGaylordError.GAYLORD_STATUS_NOT_ALLOWED_FOR_TRAILER_SCAN:
          modal = LoadToTrailerModal.GAYLORD_STATUS_NOT_ALLOWED_FOR_TRAILER_SCAN;
          break;
        case SortationGaylordError.ZONE_SKIP_GAYLORD_TRAILER_SCAN_AT_DESTINATION:
          modal = LoadToTrailerModal.ZONE_SKIP_GAYLORD_TRAILER_SCAN_AT_DESTINATION;
          break;
        case SortationGaylordError.GAYLORD_TRANSFER_NOT_COMPLETE:
          modal = LoadToTrailerModal.GAYLORD_TRANSFER_NOT_COMPLETE;
          break;
        case SortationGaylordError.QUINCE_NON_QUINCE_BOL_MISMATCH:
          modal = LoadToTrailerModal.QUINCE_NON_QUINCE_BOL_MISMATCH;
          break;
        case SortationGaylordError.QUINCE_GAYLORD_TYPE_BOL_MISMATCH:
          modal = LoadToTrailerModal.QUINCE_GAYLORD_TYPE_BOL_MISMATCH;
          break;
        case SortationGaylordError.INVALID_QUINCE_AIR_TRIP_ID_FORMAT:
          modal = LoadToTrailerModal.INVALID_QUINCE_AIR_TRIP_ID_FORMAT;
          break;
        case LoadToTrailerModal.TRAILER_ID_INPUT_MATCHES_GAYLORD_ID_FORMAT:
          modal = LoadToTrailerModal.TRAILER_ID_INPUT_MATCHES_GAYLORD_ID_FORMAT;
          break;
        case LoadToTrailerModal.TRAILER_ID_INPUT_MATCHES_BOL_ID_FORMAT:
          modal = LoadToTrailerModal.TRAILER_ID_INPUT_MATCHES_BOL_ID_FORMAT;
          break;
        case LoadToTrailerModal.INVALID_BOL:
          modal = LoadToTrailerModal.INVALID_BOL;
          break;
        case LoadToTrailerModal.INVALID_BOL_ID:
          modal = LoadToTrailerModal.INVALID_BOL_ID;
          break;
        case LoadToTrailerModal.MISSING_MAIL_ID_FOR_AIR_SHIPMENT:
          modal = LoadToTrailerModal.MISSING_MAIL_ID_FOR_AIR_SHIPMENT;
          break;
        default:
          modal = LoadToTrailerModal.UNKNOWN_ERROR;
          break;
      }

      emitFlash("DANGER");
      showModal(modal, loadToTrailerMessageMap[modal]);
    },
    [emitFlash, showModal]
  );

  const loadGaylordToTrailer = useCallback(
    async (gaylordBarcode: string, quinceTripId?: string, partner?: MiddleMilePartner) => {
      try {
        await sortationClient.processLoadToTrailerScan(gaylordBarcode, bolId, sortationId!, quinceTripId, partner);
        successResponse();
        addAutoCloseNotification(
          createSuccessNotification(`${gaylordBarcode} scanned to BOL & Trailer.`),
          TOAST_AUTO_CLOSE
        );
        setGaylordId("");
      } catch (err) {
        handleError(err);
      }
    },
    [sortationClient, bolId, sortationId, successResponse, addAutoCloseNotification, handleError]
  );

  const [onGaylordIdSubmitState, onGaylordIdSubmit] = useAsyncFn(
    async (input: string) => {
      resetNotifications();

      if (!isGaylord(input)) {
        if (isValidShipwellBolId(input)) {
          return await onBolIdSubmit(input);
        }
        handleError(new Error(LoadToTrailerModal.INVALID_GAYLORD_ID));
        return;
      }

      setGaylordId(input);

      try {
        const gaylord = await sortationClient.getGaylord(input);

        if (isQuinceSortCode(gaylord?.sortCode)) {
          const isAir = !!gaylord.isCartonToPalletGaylord;
          const mailId = gaylord.externalNotificationId ?? undefined;
          if (isAir && !mailId) {
            handleError(new Error(LoadToTrailerModal.MISSING_MAIL_ID_FOR_AIR_SHIPMENT));
            return;
          }
          const mappingParams = isAir ? { sortCode: gaylord.sortCode!, mailId: mailId! } : { bolId };

          try {
            const mapping = await sortationClient.getQuinceManifestMapping(mappingParams);
            if (mapping?.tripId) {
              await loadGaylordToTrailer(input, mapping.tripId, mapping.middleMilePartner);
              return;
            }
          } catch {
            // Mapping not found — prompt for tripId
          }

          pendingGaylordIdRef.current = input;
          pendingIsAirRef.current = isAir;
          tripIdLabelRef.current = isAir ? "Enter AWB Number" : "Enter Booking ID";
          setTripId("");
          setLoadToTrailerScanState(LoadToTrailerScanState.AWAITING_TRIP_ID_INPUT);
          infoResponse();
          addAutoCloseNotification(
            createSuccessNotification(`Quince gaylord detected. Please enter ${isAir ? "AWB number" : "booking ID"}.`),
            TOAST_AUTO_CLOSE
          );
          return;
        }

        await loadGaylordToTrailer(input);
      } catch (err) {
        handleError(err);
      }
    },
    [
      addAutoCloseNotification,
      infoResponse,
      sortationClient,
      handleError,
      setGaylordId,
      sortationId,
      bolId,
      loadGaylordToTrailer,
    ]
  );

  const [onTripIdSubmitState, onTripIdSubmit] = useAsyncFn(
    async (input: string) => {
      if (!input.length) {
        return;
      }
      resetNotifications();

      const gaylordBarcode = pendingGaylordIdRef.current;

      if (pendingIsAirRef.current) {
        // Air shipments use Clearjet; ops doesn't pick a partner.
        await loadGaylordToTrailer(gaylordBarcode, input, MiddleMilePartner.CLEARJET);
        pendingGaylordIdRef.current = "";
        setTripId("");
        setLoadToTrailerScanState(LoadToTrailerScanState.AWAITING_GAYLORD_SCAN);
        return;
      }

      // Ground first scan — open the partner-selection modal; submit happens on confirm.
      showModal(LoadToTrailerModal.MIDDLE_MILE_PARTNER_SELECTION, {
        bolId,
        gaylordId: gaylordBarcode,
        tripId: input,
        options: GROUND_MIDDLE_MILE_PARTNER_OPTIONS,
        onConfirm: async (partner: MiddleMilePartner) => {
          try {
            await loadGaylordToTrailer(gaylordBarcode, input, partner);
          } finally {
            hideModal(LoadToTrailerModal.MIDDLE_MILE_PARTNER_SELECTION);
            pendingGaylordIdRef.current = "";
            setTripId("");
            setLoadToTrailerScanState(LoadToTrailerScanState.AWAITING_GAYLORD_SCAN);
          }
        },
      });
    },
    [loadGaylordToTrailer, resetNotifications, showModal, hideModal, bolId]
  );

  const [onBolIdSubmitState, onBolIdSubmit] = useAsyncFn(
    async (input: string) => {
      const ctx = logStart({ fn: "onBolIdSubmit", input });

      if (!input.length) {
        return;
      }
      resetNotifications();

      if (!isValidShipwellBolId(input)) {
        handleError(new Error(LoadToTrailerModal.INVALID_BOL_ID));
        return;
      }

      try {
        const existingBol = await sortationClient.getBillOfLading(input);
        if (!existingBol) {
          handleError(new Error(LoadToTrailerModal.INVALID_BOL_ID));
          return;
        }

        if (!existingBol.trailerId) {
          handleError(new Error(LoadToTrailerModal.INVALID_BOL));
          return;
        }

        setBolId(input);
        setTrailerId(existingBol.trailerId);
        setGaylordId("");
        setLoadToTrailerScanState(LoadToTrailerScanState.AWAITING_GAYLORD_SCAN);

        infoResponse();
        addAutoCloseNotification(
          createSuccessNotification(`BOL ${input} successfully scanned. Please continue scanning gaylords.`),
          TOAST_AUTO_CLOSE
        );
        setNavbar({
          title: "Bol/Trailer Scan",
          rightAction: TextButton({ onClick: resetPage, defaultMessage: "Reset BOL/Trailer" }),
        });
      } catch (err) {
        if (err.message === BillOfLadingError.BOL_NOT_FOUND) {
          setBolId(input);
          setTrailerId("");
          setGaylordId("");
          setLoadToTrailerScanState(LoadToTrailerScanState.AWAITING_TRAILER_INPUT);

          infoResponse();
          addAutoCloseNotification(
            createSuccessNotification(`BOL ${input} successfully scanned. Please enter Truck/Trailer ID.`),
            TOAST_AUTO_CLOSE
          );
          return;
        }
        logError(ctx, err);
        handleError(new Error(LoadToTrailerModal.UNKNOWN_ERROR));
      }
    },
    [bolId, setBolId, trailerId, setTrailerId, resetNotifications, handleError, setBolId, resetPage]
  );

  const [onTrailerIdSubmitState, onTrailerIdSubmit] = useAsyncFn(
    async (input: string) => {
      const ctx = logStart({ fn: "onTrailerIdSubmit", input });

      if (!input.length) {
        return;
      }

      if (isGaylord(input)) {
        handleError(new Error(LoadToTrailerModal.TRAILER_ID_INPUT_MATCHES_GAYLORD_ID_FORMAT));
        return;
      }

      if (isValidShipwellBolId(input)) {
        handleError(new Error(LoadToTrailerModal.TRAILER_ID_INPUT_MATCHES_BOL_ID_FORMAT));
        return;
      }

      resetNotifications();

      try {
        await sortationClient.createOrUpdateBillOfLading(bolId, input);

        setTrailerId(input);
        setLoadToTrailerScanState(LoadToTrailerScanState.AWAITING_GAYLORD_SCAN);
        infoResponse();
        addAutoCloseNotification(
          createSuccessNotification(`BOL ${bolId} successfully saved to system. You may now scan gaylords to BOL.`),
          TOAST_AUTO_CLOSE
        );
        setNavbar({
          title: "Bol/Trailer Scan",
          rightAction: TextButton({ onClick: resetPage, defaultMessage: "Reset BOL/Trailer" }),
        });
      } catch (err) {
        logError(ctx, err);
        handleError(new Error(LoadToTrailerModal.UNKNOWN_ERROR));
      }
    },
    [trailerId, setTrailerId, trailerId, setTrailerId, resetNotifications, handleError, setBolId, resetPage, bolId]
  );

  const scanConfig = useMemo(() => {
    switch (loadToTrailerScanState) {
      case LoadToTrailerScanState.AWAITING_BOL_SCAN:
        return {
          title: "Scan BOL QR Code",
          label: "BOL ID",
          value: bolId,
          onSubmit: onBolIdSubmit,
          onChange: setBolId,
          disabled: onBolIdSubmitState.loading,
        };
      case LoadToTrailerScanState.AWAITING_TRAILER_INPUT:
        return {
          title: "Type in Truck/Trailer ID",
          label: "Truck/Trailer ID",
          value: trailerId,
          onSubmit: onTrailerIdSubmit,
          onChange: setTrailerId,
          disabled: onTrailerIdSubmitState.loading,
        };
      case LoadToTrailerScanState.AWAITING_GAYLORD_SCAN:
        return {
          title: "Scan Gaylord Barcode",
          label: "Gaylord ID",
          value: gaylordId,
          onSubmit: onGaylordIdSubmit,
          onChange: setGaylordId,
          disabled: onGaylordIdSubmitState.loading,
        };
      case LoadToTrailerScanState.AWAITING_TRIP_ID_INPUT:
        return {
          title: tripIdLabelRef.current,
          label: "Trip ID",
          value: tripId,
          onSubmit: onTripIdSubmit,
          onChange: setTripId,
          disabled: onTripIdSubmitState.loading,
        };
    }
  }, [
    loadToTrailerScanState,
    bolId,
    onBolIdSubmit,
    setBolId,
    onBolIdSubmitState,
    trailerId,
    onTrailerIdSubmit,
    setTrailerId,
    onTrailerIdSubmitState,
    gaylordId,
    onGaylordIdSubmit,
    setGaylordId,
    onGaylordIdSubmitState,
    tripId,
    onTripIdSubmit,
    onTripIdSubmitState,
  ]);

  return {
    bolId,
    trailerId,
    scanConfig,
    loadToTrailerScanState,
  };
};
